// src/components/auth/RegisterForm.jsx

import React, { useState, useEffect } from "react";
import axios from "../../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import zxcvbn from "zxcvbn";

/* ─── Responsive CSS injected once ───────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.jb-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #5A1010 0%, #3D0A0A 45%, #1A0505 100%);
  padding: clamp(12px, 4vw, 32px) clamp(12px, 4vw, 24px)  clamp(30px, 6vw, 60px);;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow-x: hidden;
}

.jb-watermark {
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background-image: radial-gradient(circle at 50% 50%, #C9962A 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

/* ── Card ── */
.jb-card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 980px;
  border-radius: clamp(12px, 2vw, 20px);
  overflow: hidden;
  margin-top: 70px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.6);
}

/* ── Left panel ── */
.jb-left {
  flex: 0 0 42%;
  background: linear-gradient(160deg, #7B1C1C 0%, #5A1010 100%);
  padding: clamp(28px, 5vw, 56px) clamp(20px, 4vw, 44px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.jb-om {
  font-size: clamp(28px, 4vw, 40px);
  color: #C9962A;
  margin-bottom: clamp(10px, 2vw, 18px);
  line-height: 1;
}

.jb-tagline {
  font-family: 'Playfair Display', serif;
  font-size: clamp(18px, 2.6vw, 30px);
  font-weight: 700;
  color: #FDF6EC;
  line-height: 1.4;
  margin-bottom: clamp(12px, 2vw, 20px);
}

.jb-tagline span { color: #E8C46A; }

.jb-divrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: clamp(12px, 2vw, 22px) 0;
  width: 100%;
}
.jb-divline { flex: 1; height: 1px; background: #C9962A; opacity: 0.35; }
.jb-divsym { color: #C9962A; font-size: 16px; user-select: none; }

.jb-sub {
  font-size: clamp(11.5px, 1.3vw, 13.5px);
  color: #F5EDD6;
  opacity: 0.85;
  line-height: 1.7;
  max-width: 280px;
}

.jb-pills { list-style: none; margin-top: clamp(14px, 2vw, 24px); display: flex; flex-direction: column; gap: 9px; }
.jb-pill  { display: flex; align-items: center; gap: 9px; font-size: clamp(11px, 1.2vw, 13px); color: #FDF6EC; opacity: 0.9; }
.jb-dot   { width: 6px; height: 6px; border-radius: 50%; background: #C9962A; flex-shrink: 0; }

/* ── Right panel (form) ── */
.jb-right {
  flex: 1;
  background: #FDF6EC;
  padding: clamp(24px, 4vw, 48px) clamp(20px, 4vw, 44px);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.jb-fhead { text-align: center; margin-bottom: clamp(14px, 2vw, 24px); }
.jb-ftitle {
  font-family: 'Playfair Display', serif;
  font-size: clamp(18px, 2.2vw, 24px);
  font-weight: 700;
  color: #7B1C1C;
  margin-bottom: 4px;
}
.jb-fsub { font-size: clamp(11px, 1.2vw, 13px); color: #8B7355; }

.jb-lotus { text-align: center; margin-bottom: clamp(14px, 2vw, 20px); }

/* ── Form layout ── */
.jb-row2 {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}
.jb-fw { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.jb-mb { margin-bottom: 10px; }

.jb-input {
  padding: clamp(8px, 1.2vw, 10px) 12px;
  border: 1.5px solid #D9C8A8;
  border-radius: 7px;
  font-size: clamp(12.5px, 1.3vw, 14px);
  color: #2C1A0E;
  background: #fff;
  width: 100%;
  outline: none;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.18s;
  -webkit-appearance: none;
  appearance: none;
}
.jb-input:focus { border-color: #C9962A; }
.jb-input.err    { border-color: #C0392B; }

.jb-err  { font-size: 11px; color: #C0392B; margin-top: 3px; }

.jb-meter-bar { height: 4px; border-radius: 3px; margin-top: 5px; width: 100%; transition: background 0.3s; }
.jb-meter-lbl { font-size: 11px; color: #8B7355; margin-top: 2px; }

.jb-btn {
  margin-top: 10px;
  padding: clamp(10px, 1.5vw, 13px);
  background: linear-gradient(90deg, #7B1C1C, #3D0A0A);
  border: 1.5px solid #C9962A;
  border-radius: 7px;
  color: #FDF6EC;
  font-size: clamp(13px, 1.4vw, 15px);
  font-weight: 600;
  letter-spacing: 0.4px;
  cursor: pointer;
  width: 100%;
  font-family: 'Playfair Display', serif;
  transition: opacity 0.18s;
}
.jb-btn:hover:not(:disabled) { opacity: 0.88; }
.jb-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.jb-tos { font-size: 11px; color: #8B7355; text-align: center; margin-top: 10px; }
.jb-tos a { color: #7B1C1C; text-decoration: underline; }
.jb-signin { font-size: 12px; color: #8B7355; text-align: center; margin-top: 8px; }
.jb-signin a { color: #7B1C1C; font-weight: 600; text-decoration: underline; }

/* ════════════════════════════════════════════════════
   BREAKPOINTS
   ════════════════════════════════════════════════════ */

/* Tablet: hide left panel tagline pills, compress */
@media (max-width: 768px) {
  .jb-card { flex-direction: column; max-width: 520px; }
  .jb-left {
    flex: none;
    padding: 24px 24px 20px;
    align-items: center;
    text-align: center;
  }
  .jb-tagline { font-size: clamp(17px, 5vw, 22px); }
  .jb-sub, .jb-pills { display: none; }
  .jb-om { margin-bottom: 8px; }
  .jb-divrow { display: none; }
  .jb-right { padding: 24px 20px 28px; }
}

/* Mobile ≤ 480px: single column names */
@media (max-width: 480px) {
  .jb-row2 { flex-direction: column; gap: 0; margin-bottom: 0; }
  .jb-row2 .jb-fw { margin-bottom: 10px; }
  .jb-left { padding: 18px 16px 14px; }
  .jb-tagline { font-size: 16px; }
  .jb-om { font-size: 24px; }
  .jb-right { padding: 20px 16px 24px; }
  .jb-ftitle { font-size: 17px; }
}

/* Very small phones (320px) */
@media (max-width: 360px) {
  .jb-page { padding: 0; align-items: flex-start; }
  .jb-card { border-radius: 0; box-shadow: none; }
  .jb-btn  { font-size: 13px; }
}

/* Large desktop ≥ 1280px */
@media (min-width: 1280px) {
  .jb-card { max-width: 1060px; }
  .jb-left { flex: 0 0 40%; }
  .jb-tagline { font-size: 32px; }
}
`;

/* ─── Password strength map ───────────────────────────────────────────────── */
const strengthMap = [
  { label: "Very Weak", color: "#E74C3C" },
  { label: "Weak", color: "#E67E22" },
  { label: "Fair", color: "#F1C40F" },
  { label: "Good", color: "#27AE60" },
  { label: "Strong", color: "#1E8449" },
];

/* ─── Lotus SVG divider ───────────────────────────────────────────────────── */
const LotusDivider = () => (
  <svg
    width="120"
    height="24"
    viewBox="0 0 120 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <line
      x1="0"
      y1="12"
      x2="40"
      y2="12"
      stroke="#C9962A"
      strokeWidth="0.8"
      strokeOpacity="0.45"
    />
    <ellipse
      cx="60"
      cy="14"
      rx="5"
      ry="8"
      fill="#C9962A"
      opacity="0.85"
      transform="rotate(-20 60 14)"
    />
    <ellipse
      cx="60"
      cy="14"
      rx="5"
      ry="8"
      fill="#C9962A"
      opacity="0.85"
      transform="rotate(20 60 14)"
    />
    <ellipse cx="60" cy="13" rx="5" ry="9" fill="#C9962A" opacity="0.9" />
    <ellipse
      cx="54"
      cy="16"
      rx="4"
      ry="6"
      fill="#C9962A"
      opacity="0.55"
      transform="rotate(-38 54 16)"
    />
    <ellipse
      cx="66"
      cy="16"
      rx="4"
      ry="6"
      fill="#C9962A"
      opacity="0.55"
      transform="rotate(38 66 16)"
    />
    <ellipse cx="60" cy="22" rx="6" ry="2.5" fill="#C9962A" opacity="0.3" />
    <line
      x1="80"
      y1="12"
      x2="120"
      y2="12"
      stroke="#C9962A"
      strokeWidth="0.8"
      strokeOpacity="0.45"
    />
  </svg>
);

/* ─── Component ───────────────────────────────────────────────────────────── */
const RegisterForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    jain_sect: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordScore, setPasswordScore] = useState(-1);
  const [focused, setFocused] = useState(null);

  /* inject CSS once */
  useEffect(() => {
    const id = "jb-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleChange = ({ target: { name, value } }) => {
    setFormData((p) => ({ ...p, [name]: value }));
    if (name === "email") {
      setErrors((p) =>
        value && !validateEmail(value)
          ? { ...p, email: "Invalid email format." }
          : (({ email, ...r }) => r)(p),
      );
    }
    if (name === "password") {
      setPasswordScore(value ? zxcvbn(value).score : -1);
      setErrors((p) =>
        value.length < 6 && value
          ? { ...p, password: "At least 6 characters required." }
          : (({ password, ...r }) => r)(p),
      );
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!formData.first_name.trim())
      errs.first_name = "First name is required.";
    if (!formData.last_name.trim()) errs.last_name = "Surname is required.";
    if (!validateEmail(formData.email)) errs.email = "Enter a valid email.";
    if (formData.password.length < 6)
      errs.password = "At least 6 characters required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post("/api/accounts/register/", formData);
      toast.success(
        res.data.is_existing_unverified
          ? "An account with this email already exists but wasn't verified. We've sent a new OTP — please verify to continue."
          : `Registration successful! Your username is "${res.data.username}". Please verify the OTP sent to your email.`,
      );
      localStorage.setItem("pendingUserEmail", formData.email);
      navigate("/verify-otp");
    } catch (err) {
      toast.error(
        err.response?.data?.error ?? "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const ic = (name) =>
    ["jb-input", errors[name] ? "err" : "", focused === name ? "focused" : ""]
      .filter(Boolean)
      .join(" ");

  const sm = passwordScore >= 0 ? strengthMap[passwordScore] : null;

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          style: { fontFamily: "Inter, sans-serif", fontSize: 14 },
        }}
      />

      <div className="jb-page" id="register-form">
        <div className="jb-watermark" aria-hidden="true" />

        <div className="jb-card">
          {/* ── LEFT PANEL ── */}
          <div className="jb-left">
            <div className="jb-om" aria-label="Om">
              ꣽ
            </div>

            <h1 className="jb-tagline">
              Divine Values.
              <br />
              Sacred Bond.
              <br />
              <span>Eternal Together.</span>
            </h1>

            <div className="jb-divrow" aria-hidden="true">
              <div className="jb-divline" />
              <span className="jb-divsym">☸</span>
              <div className="jb-divline" />
            </div>

            <p className="jb-sub">
              Jain Bandhan connects souls who share Ahimsa, Satya, and Dharma —
              because the right match begins with the right principles.
            </p>

            <ul className="jb-pills" aria-label="Platform highlights">
              {[
                "Verified Jain profiles",
                "Privacy-first matchmaking",
                "Guided by Jain values",
              ].map((t) => (
                <li key={t} className="jb-pill">
                  <span className="jb-dot" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* ── RIGHT PANEL (FORM) ── */}
          <div className="jb-right">
            <div className="jb-fhead">
              <h2 className="jb-ftitle">Create Your Sacred Profile</h2>
              <p className="jb-fsub">Begin your journey with Jain Bandhan</p>
            </div>

            <div className="jb-lotus" aria-hidden="true">
              <LotusDivider />
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* First name + Last name */}
              <div className="jb-row2">
                <div className="jb-fw">
                  <input
                    className={ic("first_name")}
                    type="text"
                    name="first_name"
                    placeholder="First Name"
                    value={formData.first_name}
                    onChange={handleChange}
                    onFocus={() => setFocused("first_name")}
                    onBlur={() => setFocused(null)}
                    autoComplete="given-name"
                    required
                  />
                  {errors.first_name && (
                    <p className="jb-err">{errors.first_name}</p>
                  )}
                </div>
                <div className="jb-fw">
                  <input
                    className={ic("last_name")}
                    type="text"
                    name="last_name"
                    placeholder="Surname / Gotra"
                    value={formData.last_name}
                    onChange={handleChange}
                    onFocus={() => setFocused("last_name")}
                    onBlur={() => setFocused(null)}
                    autoComplete="family-name"
                    required
                  />
                  {errors.last_name && (
                    <p className="jb-err">{errors.last_name}</p>
                  )}
                </div>
              </div>

              {/* Username */}

              {/* Email */}
              <div className="jb-mb">
                <input
                  className={ic("email")}
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocused("email")}
                  onBlur={() => setFocused(null)}
                  autoComplete="email"
                  inputMode="email"
                  required
                />
                {errors.email && <p className="jb-err">{errors.email}</p>}
              </div>
              {/* Jain Sect */}
              <div className="jb-mb">
                <select
                  className={ic("jain_sect")}
                  name="jain_sect"
                  value={formData.jain_sect}
                  onChange={handleChange}
                  onFocus={() => setFocused("jain_sect")}
                  onBlur={() => setFocused(null)}
                >
                  <option value="">Select Jain Sect (optional)</option>
                  <option value="Digambar">Digambar</option>
                  <option value="Shwetambar">Shwetambar</option>
                  <option value="Other">Other</option>
                </select>
                {errors.jain_sect && (
                  <p className="jb-err">{errors.jain_sect}</p>
                )}
              </div>

              {/* Password */}
              <div className="jb-mb">
                <input
                  className={ic("password")}
                  type="password"
                  name="password"
                  placeholder="Create Password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused(null)}
                  autoComplete="new-password"
                  required
                />
                {errors.password && <p className="jb-err">{errors.password}</p>}
                {sm && (
                  <>
                    <div
                      className="jb-meter-bar"
                      style={{ background: sm.color }}
                    />
                    <p className="jb-meter-lbl">{sm.label} Password</p>
                  </>
                )}
              </div>

              <button type="submit" disabled={loading} className="jb-btn">
                {loading
                  ? "Creating Profile…"
                  : "🪷  Register with Jain Bandhan"}
              </button>
            </form>

            <p className="jb-tos">
              By registering you agree to our{" "}
              <a href="/terms-and-conditions" target="_blank" rel="noreferrer">
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a href="/privacy-policy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </p>

            <p className="jb-signin">
              Already have a profile? <a href="/login">Sign In</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterForm;
